import { Injectable } from '@angular/core';
import {
  DEVICE,
  NEW_RELIC_EVENT,
  PASSPORT_DATA_VALIDATION_STATUS,
  PASSPORT_DOCUMENT_TYPE,
  PASSPORT_SCAN_ERROR_SOURCE,
  PASSPORT_SCAN_ERROR_TYPE,
  PASSPORT_SCAN_PHASE,
  SCAN_RESULT,
} from '@app/enums/new-relic.enum';
import { PAX_TYPE } from '@app/enums';
import { DeviceInt } from '@app/interfaces/device.interface';
import { PassportScanInfoInt } from '@interfaces/*';
import { AdminService } from '@services/admin.service';
import { CussService } from '@services/cuss.service';
import { NewRelicEventsService } from '@services/new-relic-events.service';
import { parsePassportInfo } from '@utils/index';
import { Observable, Subject } from 'rxjs';
import { deviceIds, Device } from 'src/embross-device-manager';

const PASSPORT_SCAN_TIMEOUT_MS = 30_000;

const PASSPORT_STATUS_ERROR_MAP: Readonly<Partial<Record<number, PASSPORT_SCAN_ERROR_TYPE>>> = {
  1: PASSPORT_SCAN_ERROR_TYPE.TIMEOUT,
  3: PASSPORT_SCAN_ERROR_TYPE.CANCELLED,
  4: PASSPORT_SCAN_ERROR_TYPE.SOFTWARE_ERROR,
  6: PASSPORT_SCAN_ERROR_TYPE.SEQUENCE_ERROR,
  101: PASSPORT_SCAN_ERROR_TYPE.JAMMED,
  104: PASSPORT_SCAN_ERROR_TYPE.MEDIA_ABSENT,
  109: PASSPORT_SCAN_ERROR_TYPE.DOCUMENT_DAMAGED,
  110: PASSPORT_SCAN_ERROR_TYPE.INCOMPLETE_INSERTION,
  201: PASSPORT_SCAN_ERROR_TYPE.FORMAT_ERROR,
  302: PASSPORT_SCAN_ERROR_TYPE.HARDWARE_ERROR,
  303: PASSPORT_SCAN_ERROR_TYPE.SOFTWARE_ERROR,
  305: PASSPORT_SCAN_ERROR_TYPE.NOT_RESPONDING,
  308: PASSPORT_SCAN_ERROR_TYPE.CONFIGURATION_ERROR,
};

const PASSPORT_INFORMATIONAL_STATUS_CODES = new Set<number>([0, 102, 103, 105, 106, 107, 205]);

/** Payload emitido por OnDeviceEvent del lector de pasaporte (Embross). */
export interface PassportReaderListenPayload {
  event: PassportReaderDeviceEvent;
  passportInfo?: PassportScanInfoInt;
}

/** Evento OnDeviceEvent del lector de pasaporte (Embross). */
export interface PassportReaderDeviceEvent {
  key: string;
  value?: unknown;
}

interface PassportScanAttempt {
  id: number;
  startedAt: number;
  phase: PASSPORT_SCAN_PHASE;
  hasTerminalResult: boolean;
}

interface PassportParseResult {
  passportInfo?: PassportScanInfoInt;
  errorType: PASSPORT_SCAN_ERROR_TYPE;
  dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS;
}

interface PassportStatusDetails {
  code: number;
  isError: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PassportReaderDeviceService implements DeviceInt {
  device: Device;
  // DeviceInt exige Subject<any> (otros dispositivos usan otros payloads)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listen$: Subject<any> = new Subject<any>();
  timeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
  isComplete = false;

  private _scanAttempt: PassportScanAttempt | null = null;
  private _scanAttemptSequence = 0;
  private _scanTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor(
    private _cussService: CussService,
    private _adminService: AdminService,
    private _newRelicEventsService: NewRelicEventsService
  ) {
    this.setDevice();
  }

  disable(): void {
    if (this.hasActiveScanAttempt()) {
      this.completeScanAttempt(
        SCAN_RESULT.FAILURE,
        PASSPORT_SCAN_ERROR_TYPE.CANCELLED,
        PASSPORT_SCAN_ERROR_SOURCE.APP_LIFECYCLE,
        'disable'
      );
    }
    if (this.device) {
      this.device.disable();
      this.device.OnDeviceEvent = null;
    }
    this.clearDummyTimeout();
    this.resetScanAttempt();
  }

  enable(): void {
    this.setDevice();
    if (this.device) {
      this.device.OnDeviceEvent = this.callback;
      this.device.enable();
    }
  }

  read(): void {
    if (this.device && typeof this.device.read === 'function') {
      this.device.read();
    }
  }

  readDummy(paxType = PAX_TYPE.ADULT, firstName = '', lastName = ''): void {
    this.clearDummyTimeout();
    if (this.hasActiveScanAttempt()) {
      this.completeScanAttempt(
        SCAN_RESULT.FAILURE,
        PASSPORT_SCAN_ERROR_TYPE.CANCELLED,
        PASSPORT_SCAN_ERROR_SOURCE.APP_LIFECYCLE,
        'readDummyRestart'
      );
    }
    this.resetScanAttempt();
    this.startScanAttempt(PASSPORT_SCAN_PHASE.INSERTED);

    const firstnames = firstName ? firstName.split(' ') : [];
    const track1 = `P<USA${lastName ? lastName : 'DOE'}<<${
      firstnames?.length > 0 ? firstnames[0] : 'JOHN'
    }<${firstnames?.length > 1 ? firstnames[1] : ''}<<<<<<<<<<<<<<<<<<<<<`;
    let track2 = '';
    switch (paxType) {
      case PAX_TYPE.ADULT:
        track2 = 'N156377387USA9203273M2909100137449230<686906';
        break;
      case PAX_TYPE.CHILD:
        track2 = 'N156377387USA1503273M2909100137449230<686906';
        break;
      case PAX_TYPE.INFANT:
        track2 = 'N156377387USA2203273M2909100137449230<686906';
        break;
    }
    const passportInfo = parsePassportInfo(track1, track2, '', '');
    this.timeoutId = setTimeout(() => {
      this.timeoutId = setTimeout(() => {
        const event: PassportReaderDeviceEvent = { key: 'passportReadInternal' };
        this.updateScanPhase(PASSPORT_SCAN_PHASE.PARSING);
        this.completeScanAttempt(
          SCAN_RESULT.SUCCESS,
          PASSPORT_SCAN_ERROR_TYPE.NONE,
          PASSPORT_SCAN_ERROR_SOURCE.APP_VALIDATION,
          event.key,
          null,
          PASSPORT_DATA_VALIDATION_STATUS.COMPLETE
        );
        this.emitListen({ event, passportInfo });
      }, 1500);
    }, 1500);
  }

  getData(value: string): PassportScanInfoInt | undefined {
    if (!value) {
      return undefined;
    }

    const tracks = value.split('_@');
    return parsePassportInfo(tracks[0], tracks[1], tracks[2], tracks[3]);
  }

  // DeviceInt exige Observable<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listen(): Observable<any> {
    if (this.isComplete || this.listen$.closed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.listen$ = new Subject<any>();
      this.isComplete = false;
    }
    return this.listen$.asObservable();
  }

  callback = (event: PassportReaderDeviceEvent): void => {
    this.ensureListenSubjectOpen();
    switch (event.key) {
      case 'statusIsOK':
        break;
      case 'passportDamaged':
        if (this.hasActiveScanAttempt()) {
          this.updateScanPhase(PASSPORT_SCAN_PHASE.READING);
          this.completeScanAttempt(
            SCAN_RESULT.FAILURE,
            PASSPORT_SCAN_ERROR_TYPE.DOCUMENT_DAMAGED,
            PASSPORT_SCAN_ERROR_SOURCE.SDK_EVENT,
            event.key
          );
        }
        this.emitListen({
          passportHardwareError: true,
          errorType: 'damage',
          event,
        });
        break;
      case 'passportDataError':
        if (this.hasActiveScanAttempt()) {
          this.updateScanPhase(PASSPORT_SCAN_PHASE.READING);
          this.completeScanAttempt(
            SCAN_RESULT.FAILURE,
            PASSPORT_SCAN_ERROR_TYPE.MRZ_UNREADABLE,
            PASSPORT_SCAN_ERROR_SOURCE.SDK_EVENT,
            event.key
          );
        }
        this.emitListen({
          passportHardwareError: true,
          errorType: 'dataError',
          event,
        });
        break;
      case 'passportInserted':
        this.startScanAttempt(PASSPORT_SCAN_PHASE.INSERTED);
        this.emitListen({ event });
        break;
      case 'passportRead':
        if (this.hasActiveScanAttempt()) {
          this.updateScanPhase(PASSPORT_SCAN_PHASE.READING);
        }
        /* Mismo flujo que cuss-service.js (Angular): tras passportRead se debe invocar read()
         * para que el firmware emita passportReadInternal con los tracks. Sin esto, la UI
         * que solo escucha passportReadInternal nunca dispara lookup por nombre/destino. */
        if (this.device && typeof this.device.read === 'function') {
          this.device.read();
        }
        this.emitListen({ event });
        break;
      case 'accessingRFChip':
        if (this.hasActiveScanAttempt()) {
          this.updateScanPhase(PASSPORT_SCAN_PHASE.READING_RF_CHIP);
        }
        break;
      case 'passportReadInternal':
        this.handlePassportReadInternal(event);
        break;
      case 'passportRemoved':
        if (this.hasActiveScanAttempt()) {
          this.completeScanAttempt(
            SCAN_RESULT.FAILURE,
            PASSPORT_SCAN_ERROR_TYPE.REMOVED_BEFORE_COMPLETION,
            PASSPORT_SCAN_ERROR_SOURCE.SDK_EVENT,
            event.key
          );
        }
        break;
      case 'statusChange':
        this.handleStatusChange(event);
        break;
    }
  };

  private handlePassportReadInternal(event: PassportReaderDeviceEvent): void {
    const canCompleteMetric = this.hasActiveScanAttempt();
    if (canCompleteMetric) {
      this.updateScanPhase(PASSPORT_SCAN_PHASE.PARSING);
    }

    const rawValue = event.value;
    if (typeof rawValue !== 'string' || rawValue.trim() === '') {
      if (canCompleteMetric) {
        this.completeScanAttempt(
          SCAN_RESULT.FAILURE,
          PASSPORT_SCAN_ERROR_TYPE.EMPTY_RESPONSE,
          PASSPORT_SCAN_ERROR_SOURCE.APP_VALIDATION,
          event.key,
          null,
          PASSPORT_DATA_VALIDATION_STATUS.NOT_AVAILABLE
        );
      }
      this.emitListen({ event, passportInfo: undefined });
      return;
    }

    const parseResult = this.parseAndValidatePassport(rawValue);
    const scanResult =
      parseResult.errorType === PASSPORT_SCAN_ERROR_TYPE.NONE
        ? SCAN_RESULT.SUCCESS
        : SCAN_RESULT.FAILURE;

    if (canCompleteMetric) {
      this.completeScanAttempt(
        scanResult,
        parseResult.errorType,
        PASSPORT_SCAN_ERROR_SOURCE.APP_VALIDATION,
        event.key,
        null,
        parseResult.dataValidationStatus
      );
    }
    this.emitListen({ event, passportInfo: parseResult.passportInfo });
  }

  private parseAndValidatePassport(rawValue: string): PassportParseResult {
    const tracks = rawValue.split('_@');
    let passportInfo: PassportScanInfoInt | undefined;

    try {
      passportInfo = parsePassportInfo(tracks[0], tracks[1], tracks[2], tracks[3]);
    } catch {
      return {
        passportInfo: undefined,
        errorType: PASSPORT_SCAN_ERROR_TYPE.MALFORMED_TRACKS,
        dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS.INVALID,
      };
    }

    const hasMalformedTracks =
      tracks.length < 2 ||
      !tracks[0] ||
      !tracks[1] ||
      tracks[0].length < 5 ||
      tracks[1].length < 27;
    if (hasMalformedTracks) {
      return {
        passportInfo,
        errorType: PASSPORT_SCAN_ERROR_TYPE.MALFORMED_TRACKS,
        dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS.INVALID,
      };
    }

    const hasMissingRequiredFields =
      !passportInfo?.firstName?.trim() || !passportInfo?.lastName?.trim();
    if (hasMissingRequiredFields) {
      return {
        passportInfo,
        errorType: PASSPORT_SCAN_ERROR_TYPE.NONE,
        dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS.MISSING_REQUIRED_FIELDS,
      };
    }

    return {
      passportInfo,
      errorType: PASSPORT_SCAN_ERROR_TYPE.NONE,
      dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS.COMPLETE,
    };
  }

  private handleStatusChange(event: PassportReaderDeviceEvent): void {
    const statusDetails = this.getStatusDetails(event.value);
    if (!statusDetails) {
      return;
    }

    const { code, isError } = statusDetails;
    if (code > 300 && code < 310) {
      this._cussService.getOOSManager().completeTransaction(true);
    }

    if (!isError || !this.hasActiveScanAttempt()) {
      return;
    }

    const errorType = PASSPORT_STATUS_ERROR_MAP[code];
    if (errorType) {
      this.completeScanAttempt(
        SCAN_RESULT.FAILURE,
        errorType,
        PASSPORT_SCAN_ERROR_SOURCE.STATUS_CODE,
        event.key,
        code
      );
      return;
    }

    if (!PASSPORT_INFORMATIONAL_STATUS_CODES.has(code)) {
      this.completeScanAttempt(
        SCAN_RESULT.FAILURE,
        PASSPORT_SCAN_ERROR_TYPE.UNKNOWN_DEVICE_ERROR,
        PASSPORT_SCAN_ERROR_SOURCE.STATUS_CODE,
        event.key,
        code
      );
    }
  }

  private getStatusDetails(value: unknown): PassportStatusDetails | null {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    const code = Number(value[0]);
    if (!Number.isFinite(code)) {
      return null;
    }

    const errorFlag = value[1];
    return {
      code,
      isError: errorFlag === true || errorFlag === 1 || errorFlag === 'true',
    };
  }

  private startScanAttempt(phase: PASSPORT_SCAN_PHASE): void {
    if (this._scanAttempt && !this._scanAttempt.hasTerminalResult) {
      this._scanAttempt.phase = phase;
      return;
    }

    this.clearScanTimeout();
    this._scanAttemptSequence += 1;
    this._scanAttempt = {
      id: this._scanAttemptSequence,
      startedAt: Date.now(),
      phase,
      hasTerminalResult: false,
    };
    this.scheduleScanTimeout(this._scanAttempt.id);
  }

  private updateScanPhase(phase: PASSPORT_SCAN_PHASE): void {
    if (this._scanAttempt && !this._scanAttempt.hasTerminalResult) {
      this._scanAttempt.phase = phase;
    }
  }

  private scheduleScanTimeout(attemptId: number): void {
    this._scanTimeoutId = setTimeout(() => {
      if (this._scanAttempt?.id !== attemptId || this._scanAttempt.hasTerminalResult) {
        return;
      }

      this.completeScanAttempt(
        SCAN_RESULT.FAILURE,
        PASSPORT_SCAN_ERROR_TYPE.TIMEOUT,
        PASSPORT_SCAN_ERROR_SOURCE.LOCAL_TIMEOUT,
        'localTimeout'
      );
    }, PASSPORT_SCAN_TIMEOUT_MS);
  }

  private completeScanAttempt(
    scanResult: SCAN_RESULT,
    errorType: PASSPORT_SCAN_ERROR_TYPE,
    errorSource: PASSPORT_SCAN_ERROR_SOURCE,
    deviceEvent: string,
    deviceStatusCode: number | null = null,
    dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS = PASSPORT_DATA_VALIDATION_STATUS.NOT_AVAILABLE
  ): void {
    if (!this._scanAttempt || this._scanAttempt.hasTerminalResult) {
      return;
    }

    this._scanAttempt.hasTerminalResult = true;
    this.clearScanTimeout();
    this.emitScanMetric(
      scanResult,
      errorType,
      errorSource,
      this._scanAttempt.phase,
      deviceEvent,
      deviceStatusCode,
      dataValidationStatus,
      Date.now() - this._scanAttempt.startedAt
    );
  }

  private emitScanMetric(
    scanResult: SCAN_RESULT,
    errorType: PASSPORT_SCAN_ERROR_TYPE,
    errorSource: PASSPORT_SCAN_ERROR_SOURCE,
    scanPhase: PASSPORT_SCAN_PHASE,
    deviceEvent: string,
    deviceStatusCode: number | null,
    dataValidationStatus: PASSPORT_DATA_VALIDATION_STATUS,
    scanDurationMs: number
  ): void {
    try {
      const isFailure = scanResult === SCAN_RESULT.FAILURE;
      this._newRelicEventsService.trackEvent(NEW_RELIC_EVENT.PASSPORT_SCANNER_RESULT, {
        scan_result: scanResult,
        scan_duration_ms: scanDurationMs,
        error_type: errorType,
        error_source: errorSource,
        scan_phase: scanPhase,
        data_validation_status: dataValidationStatus,
        document_type: PASSPORT_DOCUMENT_TYPE.PASSPORT,
        device: DEVICE.PASSPORT_READER,
        device_event: deviceEvent,
        device_status_code: deviceStatusCode,
        stationId: this._adminService.admin?.Location || '',
        isError: isFailure,
        error_message: isFailure ? errorType : '',
      });
    } catch (error) {
      console.warn('[PassportReaderDeviceService] Error emitting scan metric', error);
    }
  }

  private hasActiveScanAttempt(): boolean {
    return Boolean(this._scanAttempt && !this._scanAttempt.hasTerminalResult);
  }

  private resetScanAttempt(): void {
    this.clearScanTimeout();
    this._scanAttempt = null;
  }

  private clearScanTimeout(): void {
    if (this._scanTimeoutId !== undefined) {
      clearTimeout(this._scanTimeoutId);
      this._scanTimeoutId = undefined;
    }
  }

  private clearDummyTimeout(): void {
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  private ensureListenSubjectOpen(): void {
    if (this.listen$.closed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.listen$ = new Subject<any>();
      this.isComplete = false;
    }
  }

  private emitListen(payload: unknown): void {
    try {
      this.listen$.next(payload);
    } catch (error) {
      console.error('[PassportReaderDeviceService] listen$ subscriber error', error);
    }
  }

  private setDevice(): void {
    if (this._cussService.getDeviceManager()) {
      this.device = this._cussService.getDeviceManager().getDevice(deviceIds.PASSPORT_READER);
    }
  }
}
